import { DateTime } from 'luxon';
import { useI18nContext } from '@/i18n/i18n-react';
import { Button } from '@/components/ui/button';
import {
  CalendarArrowUp,
  CalendarArrowDown,
  ChevronDown,
  Check,
  CalendarRange,
  ArrowRight,
  X,
} from 'lucide-react';
import { cn, getLuxonLocale } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { IconedInput } from './iconed-input';

interface BaseDateRangeFilterProps {
  fromDate: string | null | undefined;
  toDate: string | null | undefined;
  predefinedDateRange: string | undefined;
  setPredefinedDateRange: (range: string | undefined) => void;
  predefinedDateRanges: {
    label: string;
    value: string;
    from: string;
    to: string;
  }[];
  defaultDateRange: string | undefined;
}

type StrictDateRangeFilterProps = BaseDateRangeFilterProps & {
  allowOpenEnded?: false;
  setFromDate: (date: string) => void;
  setToDate: (date: string) => void;
};

/** Props of the strict (non open-ended) filter: also the `PeriodFilter` contract. */
export type PeriodFilterProps = StrictDateRangeFilterProps;

type OpenEndedDateRangeFilterProps = BaseDateRangeFilterProps & {
  allowOpenEnded: true;
  setFromDate: (date: string | null) => void;
  setToDate: (date: string | null) => void;
};

type DateRangeFilterProps =
  | StrictDateRangeFilterProps
  | OpenEndedDateRangeFilterProps;

export function DateRangeFilter({
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  predefinedDateRanges,
  predefinedDateRange,
  setPredefinedDateRange,
  defaultDateRange,
  allowOpenEnded = false,
}: DateRangeFilterProps) {
  const { LL, locale } = useI18nContext();

  const defaultFromDate =
    predefinedDateRanges.find((pdr) => pdr.value === defaultDateRange)?.from ??
    DateTime.now().minus({ hours: 1 }).toISO();
  const defaultToDate =
    predefinedDateRanges.find((pdr) => pdr.value === defaultDateRange)?.to ??
    DateTime.now().toISO();

  const commitFromDate = (date: string | null) => {
    if (allowOpenEnded) {
      (setFromDate as (value: string | null) => void)(date);
      return;
    }

    setFromDate(date ?? defaultFromDate);
  };

  const commitToDate = (date: string | null) => {
    if (allowOpenEnded) {
      (setToDate as (value: string | null) => void)(date);
      return;
    }

    setToDate(date ?? defaultToDate);
  };

  const handlePredefinedDateRangeChange = (value: string) => {
    const range = predefinedDateRanges.find((r) => r.value === value);
    if (!range) return;
    setPredefinedDateRange(value);
    commitFromDate(range.from);
    commitToDate(range.to);
  };

  const handleCustomDateChange = (from: string | null, to: string | null) => {
    setPredefinedDateRange(undefined);
    commitFromDate(from);
    commitToDate(to);
  };

  const formattedFromDate = fromDate
    ? DateTime.fromISO(fromDate)
        .setLocale(getLuxonLocale(locale))
        .toLocaleString()
    : '—';

  const formattedToDate = toDate
    ? DateTime.fromISO(toDate)
        .setLocale(getLuxonLocale(locale))
        .toLocaleString()
    : '—';

  const selectedRangeLabel = predefinedDateRanges?.find(
    (g) => g.value === predefinedDateRange
  )?.label;

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button className={cn('justify-between')} variant="outline">
            <>
              <CalendarRange />
              <p className="text-ellipsis overflow-hidden whitespace-nowrap">
                {selectedRangeLabel ||
                  `${formattedFromDate} - ${formattedToDate}`}
              </p>
              <ChevronDown />
            </>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex flex-row p-0 overflow-hidden">
          <Command className="rounded-none">
            <CommandList className="max-h-none">
              <CommandGroup>
                {predefinedDateRanges?.map((g) => (
                  <CommandItem
                    className="cursor-pointer"
                    onSelect={() => {
                      handlePredefinedDateRangeChange(g.value);
                    }}
                  >
                    {g.label}
                    <Check
                      className={cn(
                        'ml-auto',
                        predefinedDateRange === g.value
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          <div className="flex flex-col p-2.5 w-full border-l border-input">
            <div className="flex flex-row gap-2 items-center">
              <IconedInput
                leftIcon={<CalendarArrowUp width={20} height={20} />}
                readOnly
                value={
                  fromDate
                    ? `${LL.salesPage.filters.from()} ${DateTime.fromISO(fromDate).setLocale(getLuxonLocale(locale)).toLocaleString()}`
                    : ''
                }
                placeholder={LL.salesPage.filters.from()}
                className={cn(
                  'pl-8 cursor-pointer group-data-[state=open]:ring-ring group-data-[state=open]:ring-2 group-data-[state=open]:ring-offset-2'
                )}
                rightIcon={
                  allowOpenEnded && fromDate ? (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={(event) => {
                        event.stopPropagation();
                        setPredefinedDateRange(undefined);
                        commitFromDate(null);
                      }}
                    >
                      <X width={16} height={16} />
                    </button>
                  ) : undefined
                }
              />
              <ArrowRight size={40} />
              <IconedInput
                leftIcon={<CalendarArrowDown width={20} height={20} />}
                value={
                  toDate
                    ? `${LL.salesPage.filters.to()} ${DateTime.fromISO(toDate).setLocale(getLuxonLocale(locale)).toLocaleString()}`
                    : ''
                }
                placeholder={LL.salesPage.filters.to()}
                readOnly
                className={cn(
                  'group-data-[state=open]:ring-ring group-data-[state=open]:ring-2 group-data-[state=open]:ring-offset-2'
                )}
                rightIcon={
                  allowOpenEnded && toDate ? (
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={(event) => {
                        event.stopPropagation();
                        setPredefinedDateRange(undefined);
                        commitToDate(null);
                      }}
                    >
                      <X width={16} height={16} />
                    </button>
                  ) : undefined
                }
              />
            </div>
            <div className="flex flex-row justify-between">
              <DatePicker
                toDate={
                  toDate ? DateTime.fromISO(toDate).toJSDate() : undefined
                }
                value={
                  fromDate ? DateTime.fromISO(fromDate).toJSDate() : undefined
                }
                onChange={(value) => {
                  if (!value && !allowOpenEnded) return;
                  handleCustomDateChange(
                    value ? DateTime.fromJSDate(value).toISO() : null,
                    toDate ?? null
                  );
                }}
              />

              <DatePicker
                fromDate={
                  fromDate ? DateTime.fromISO(fromDate).toJSDate() : undefined
                }
                toDate={DateTime.now().toJSDate()}
                value={toDate ? DateTime.fromISO(toDate).toJSDate() : undefined}
                onChange={(value) => {
                  if (!value && !allowOpenEnded) return;
                  handleCustomDateChange(
                    fromDate ?? null,
                    value
                      ? DateTime.fromJSDate(value).endOf('day').toISO()
                      : null
                  );
                }}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}
